import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function TableConfirmModal({ tableNumber, onConfirm, onChange }) {
    const [customTable, setCustomTable] = useState(tableNumber || '');
    const [isChanging, setIsChanging] = useState(!tableNumber);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Slight delay for smooth appearance after page loads
        const timer = setTimeout(() => setVisible(true), 400);
        return () => clearTimeout(timer);
    }, []);

    const handleConfirm = () => {
        const table = isChanging ? customTable.trim() : tableNumber;
        sessionStorage.setItem('confirmed_table', table);
        onConfirm(table);
    };

    return (
        <AnimatePresence>
            {visible && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-charcoal/40 backdrop-blur-sm z-[300]"
                    />

                    {/* Bottom Sheet */}
                    <motion.div
                        initial={{ y: '100%', opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: '100%', opacity: 0 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 z-[301] bg-white rounded-t-3xl shadow-2xl px-6 pt-5 pb-8 max-w-lg mx-auto"
                    >
                        {/* Handle */}
                        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />

                        {!isChanging ? (
                            <>
                                <div className="flex items-center gap-3 mb-1">
                                    <span className="text-3xl">🪑</span>
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Your Seat</p>
                                        <h2 className="text-2xl font-black text-charcoal">Table {tableNumber}</h2>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-500 mb-6 ml-12">Is this the correct table?</p>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setIsChanging(true)}
                                        className="flex-1 py-3.5 rounded-2xl border-2 border-slate-200 font-bold text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-colors text-sm"
                                    >
                                        Change
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        className="flex-[2] py-3.5 rounded-2xl bg-primary text-charcoal font-black text-sm shadow-lg shadow-primary/30 hover:bg-[#F0C900] active:scale-[0.98] transition-colors"
                                    >
                                        ✓ Yes, Confirm
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-3 mb-5">
                                    <span className="text-3xl">📍</span>
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Enter Table</p>
                                        <h2 className="text-xl font-black text-charcoal">Which table are you at?</h2>
                                    </div>
                                </div>

                                <input
                                    autoFocus
                                    type="text"
                                    value={customTable}
                                    onChange={e => setCustomTable(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && customTable.trim() && handleConfirm()}
                                    placeholder="e.g. 5, A4, Window Seat"
                                    className="input-field text-lg font-black mb-4"
                                />

                                <button
                                    onClick={handleConfirm}
                                    disabled={!customTable.trim()}
                                    className={`w-full py-4 rounded-2xl font-black text-sm transition-colors ${
                                        customTable.trim()
                                            ? 'bg-primary text-charcoal shadow-lg shadow-primary/30 hover:bg-[#F0C900] active:scale-[0.98]'
                                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    }`}
                                >
                                    Confirm Table
                                </button>

                                {tableNumber && (
                                    <button
                                        onClick={() => setIsChanging(false)}
                                        className="w-full mt-3 py-3 text-sm font-bold text-slate-500 hover:text-charcoal transition-colors"
                                    >
                                        ← Back
                                    </button>
                                )}
                            </>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
